import { ReactElement } from 'react';

import { DynamicFormSchema, StepIndicatorConfig, StepIndicatorNumberedOptions } from './types/DynamicFormTypes';
import { cssprefix } from './utils/utils';

interface StepIndicatorProps extends StepIndicatorConfig {
	schema: DynamicFormSchema;
	currentStep: number;
}

interface ItemProps {
	key: string;
	idx: number;
	stepTitle: string;
}

interface CurrOnlyItemProps extends ItemProps {
	len: number;
	numbered?: StepIndicatorNumberedOptions;
}

const StepListItem = ({ key, idx, stepTitle }: ItemProps) => (
	<li key={key} className={`${cssprefix}i${idx}`}>
		<span className={`${cssprefix}title`}>{stepTitle}</span>
	</li>
);

const CurrOnly = ({ key, idx, stepTitle, len, numbered }: CurrOnlyItemProps) => {
	let stepString = '';

	if (numbered === true) {
		stepString = `${idx + 1} of ${len}`;
	} else if (numbered && typeof numbered === 'object') {
		stepString = `${numbered.prefix ?? ''} ${idx + 1} of ${len} ${numbered.postfix ?? ''}`;
	}

	return (
		<span key={key} className={`${cssprefix}i${idx}`}>
			<span className={`${cssprefix}number`}>{stepString}</span>
			<span className={`${cssprefix}title`}>{stepTitle}</span>
		</span>
	);
};

interface RenderTypeFn {
	schema: DynamicFormSchema;
	classes: string;
	numbered?: StepIndicatorNumberedOptions;
}

type RenderOptionsType = Record<string, (arg: RenderTypeFn) => ReactElement | null>;

const RenderOptions: RenderOptionsType = {
	numberedOrTitledList: ({ schema, classes }: RenderTypeFn) => (
		<ol className={`${cssprefix}step-list ${classes}`}>
			{schema.map(({ stepTitle }, idx) => StepListItem({ idx, key: `${stepTitle}_${idx}`, stepTitle }))}
		</ol>
	),
	numberedOrTitledSingle: ({ schema, classes, numbered }: RenderTypeFn) => (
		<div className={`${cssprefix}step-current-only ${classes}`}>
			{schema.map(({ stepTitle }, idx) =>
				CurrOnly({ idx, key: `${stepTitle}_${idx}`, len: schema.length, stepTitle, numbered })
			)}
		</div>
	),
	default: (_: RenderTypeFn) => null,
};

export default function StepIndicator({
	type = 'list',
	numbered,
	titled,
	currentStep,
	schema = [],
}: StepIndicatorProps) {
	const classes = ''.concat(
		numbered ? `${cssprefix}numbered` : '',
		titled ? ` ${cssprefix}titled` : '',
		` ${cssprefix}i${currentStep}`
	);

	const renderType = ''.concat(numbered || titled ? 'numberedOrTitled' : '', type === 'list' ? 'List' : 'Single');

	return RenderOptions[RenderOptions[renderType] ? renderType : 'default']({ schema, classes, numbered });
}
